'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const certificationInfo: Record<string, string> = {
  FIATA: 'FIATA (Fédération Internationale des Associations de Transitaires et Assimilés) is the world body of the freight forwarding industry, representing around 40,000 freight forwarding and logistics firms globally.',
  SGS: 'SGS is the world\'s leading inspection, verification, testing and certification company. WTO holds SGS certification as a mark of quality and reliability in logistics services.',
  IATA: 'IATA (International Air Transport Association) is the trade association for the world\'s airlines. IATA accreditation allows WTO to issue air waybills and act as an authorized cargo agent.',
  NSBS: 'NSBS (National Suretyship and Bonding System) is a Bulgarian national system providing financial guarantees and suretyship for freight forwarders and customs agents operating in Bulgaria.'
};

const Footer: React.FC = () => {
  const [year, setYear] = useState('2026');
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setYear(new Date().getFullYear().toString());
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setActiveTooltip(null);
      }
    };
    if (activeTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeTooltip]);

  const aboutLinks = [
  { label: 'History', href: '/about/history' },
  { label: 'Mission', href: '/about/mission' },
  { label: 'Vision', href: '/about/vision' },
  { label: 'Membership', href: '/about/membership' },
  { label: 'Quality Management', href: '/about/quality-management' },
  { label: 'Business Ethics', href: '/about/business-ethics' }];


  const serviceLinks = [
  { label: 'Container Transport', href: '/container-transport' },
  { label: 'LCL Transport', href: '/lcl-transport' },
  { label: 'Airfreight', href: '/airfreight-transport' },
  { label: 'Inland Transport', href: '/inland-transport' },
  { label: 'Customs Representation', href: '/customs-representation' },
  { label: 'Port Operations', href: '/port-operations' }];


  const usefulLinks = [
  { label: 'Standard Container', href: '/useful-info#standard-container' },
  { label: 'Open Top Container', href: '/useful-info#open-top-container' },
  { label: 'Refrigerated Container', href: '/useful-info#refrigerated-container' },
  { label: 'Flat Rack Container', href: '/useful-info#flat-rack-container' },
  { label: 'Platform Container', href: '/useful-info#platform-container' },
  { label: 'Customer Survey', href: '/customer-satisfaction-survey' }];


  const certifications = ['FIATA', 'SGS', 'IATA', 'NSBS'];

  const openCookieSettings = () => {
    window.dispatchEvent(new Event('wto:open-cookie-settings'));
  };

  return (
    <footer className="relative" style={{ background: '#0F2340', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      {/* Ambient top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(27,107,138,0.5), transparent)' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-8">
        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
          {/* Brand col */}
          <div className="lg:col-span-2">
            <AppImage
              src="/assets/images/logo_letters_3_-1775650123755.png"
              alt="World Transport Overseas logo"
              width={130}
              height={38}
              className="h-9 w-auto mb-4"
              unoptimized />
            
            <p className="text-sm font-body leading-relaxed max-w-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
              World Transport Overseas — your trusted freight partner across 16+ countries. Delivering excellence in logistics since 2001.
            </p>
            {/* Social */}
            <div className="flex items-center gap-4 mt-6">
              <a href="http://www.facebook.com/pages/World-Transport-Overseas/120884564621363" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200" style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
              </a>
              <a href="https://www.linkedin.com/company/world-transport-overseas-bulgaria-ltd" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200" style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg>
              </a>
              <a href="https://twitter.com/wtobg?lang=en" target="_blank" rel="noopener noreferrer" aria-label="Twitter/X" className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200" style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </a>
            </div>
          </div>

          {/* About */}
          <div>
            <p className="text-xs uppercase tracking-widest font-body mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>About Us</p>
            <ul className="space-y-2.5">
              {aboutLinks.map((link) =>
              <li key={link.label}>
                  <Link href={link.href} className="text-sm font-body transition-colors duration-200" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {link.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Services */}
          <div>
            <p className="text-xs uppercase tracking-widest font-body mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>Services</p>
            <ul className="space-y-2.5">
              {serviceLinks.map((link) =>
              <li key={link.label}>
                  <Link href={link.href} className="text-sm font-body transition-colors duration-200" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {link.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Useful info */}
          <div>
            <p className="text-xs uppercase tracking-widest font-body mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>Useful Info</p>
            <ul className="space-y-2.5">
              {usefulLinks.map((link) =>
              <li key={link.label}>
                  <Link href={link.href} className="text-sm font-body transition-colors duration-200 hover:underline" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {link.label}
                  </Link>
                </li>
              )}
            </ul>
            <div className="mt-6">
              <Link href="/contacts" className="text-sm font-medium transition-colors duration-200 font-body" style={{ color: '#7EC8DC' }}>
                All Office Locations →
              </Link>
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div className="pt-8 mb-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-xs uppercase tracking-widest font-body mb-5 text-center" style={{ color: 'rgba(255,255,255,0.35)' }}>Certifications & Memberships</p>
          <div className="flex items-center justify-center flex-wrap gap-6" ref={tooltipRef}>
            {certifications.map((cert) =>
            <div key={cert} className="relative">
                <button
                onClick={() => setActiveTooltip(activeTooltip === cert ? null : cert)}
                className="px-5 py-2 rounded-full text-sm font-medium font-body transition-all duration-200 cursor-pointer"
                style={{
                  border: activeTooltip === cert ? '1px solid rgba(27,107,138,0.5)' : '1px solid rgba(255,255,255,0.12)',
                  background: activeTooltip === cert ? 'rgba(27,107,138,0.15)' : 'rgba(255,255,255,0.06)',
                  color: activeTooltip === cert ? '#7EC8DC' : 'rgba(255,255,255,0.55)'
                }}
                aria-expanded={activeTooltip === cert}
                aria-label={`Learn about ${cert}`}>
                
                  {cert}
                </button>
                {activeTooltip === cert &&
              <div
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-72 rounded-xl p-4 z-50 shadow-2xl"
                style={{ background: '#1A3558', border: '1px solid rgba(27,107,138,0.3)' }}>
                
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold" style={{ color: '#7EC8DC' }}>{cert}</span>
                      <button
                    onClick={() => setActiveTooltip(null)}
                    className="text-xs"
                    style={{ color: 'rgba(255,255,255,0.4)' }}
                    aria-label="Close">
                    
                        ✕
                      </button>
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      {certificationInfo[cert]}
                    </p>
                    {/* Arrow */}
                    <div
                  className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-3 h-3 rotate-45"
                  style={{ background: '#1A3558', borderRight: '1px solid rgba(27,107,138,0.3)', borderBottom: '1px solid rgba(27,107,138,0.3)' }} />
                
                  </div>
              }
              </div>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 flex flex-col md:flex-row items-center justify-between gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-xs font-body" style={{ color: 'rgba(255,255,255,0.4)' }}>
            © {year} World Transport Overseas. All Rights Reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link href="/news-promotions" className="text-xs font-body transition-colors" style={{ color: 'rgba(255,255,255,0.4)' }}>News &amp; Promotions</Link>
            <span className="w-px h-3" style={{ background: 'rgba(255,255,255,0.12)' }} />
            <Link href="/general-terms" className="text-xs font-body transition-colors" style={{ color: 'rgba(255,255,255,0.4)' }}>General Terms</Link>
            <span className="w-px h-3" style={{ background: 'rgba(255,255,255,0.12)' }} />
            <Link href="/privacy" className="text-xs font-body transition-colors" style={{ color: 'rgba(255,255,255,0.4)' }}>Privacy</Link>
            <span className="w-px h-3" style={{ background: 'rgba(255,255,255,0.12)' }} />
            <button type="button" onClick={openCookieSettings} className="text-xs font-body transition-colors" style={{ color: 'rgba(255,255,255,0.4)' }}>Cookie Settings</button>
          </div>
        </div>
      </div>
    </footer>);

};

export default Footer;
